const SOURCE = 'cart-tier-rewards';
const MAX_OPS = 12;

/* ---- module scope: survives element recreation ---- */
let reconciling = false; // in-flight guard (re-entrancy)
let rerunRequested = false; // coalesces triggers that arrive mid-flight
let settledToken = null; // cart signature already reconciled
let scheduled = false; // coalesces bursts of triggers into one pass
let opsBudget = MAX_OPS;
let lastProgress = 0; // fill animation from-value
const failedVariants = new Set(); // 422'd variants, never retried this page load

let lastReachedTierIds = null;
const CONFETTI_COLORS = ['#2FA84F', '#3B5BDB', '#7048E8', '#FFC933', '#1A1A1A'];

function readPayload(root) {
  try {
    const script = root.querySelector(':scope > script[data-cart-tier-rewards]');
    return script ? JSON.parse(script.textContent) : null;
  } catch (error) {
    console.warn('[cart-tier-rewards] malformed payload:', error.message);
    return null;
  }
}

/* These two must stay equivalent to the rule in component-cart-drawer.liquid.
   If they drift, the bar and the gifts disagree about what is unlocked. */
function isGiftLine(item, config) {
  return item?.properties?.[config.giftProperty] === 'true';
}

function isExcludedLine(item, config) {
  const title = (item.title || '').toUpperCase();
  const vendor = (item.vendor || '').toLowerCase();
  return (
    config.excludeTitles.some((needle) => title.includes(needle)) ||
    config.excludeVendors.includes(vendor)
  );
}

function qualifyingTotal(cart, config) {
  const lineTotal = cart.items.reduce(
    (sum, item) =>
      isGiftLine(item, config) || isExcludedLine(item, config) ? sum : sum + item.final_line_price,
    0
  );

  return Math.max(0, lineTotal - (cart.total_discount || 0));
}

/* Signature of everything reconciliation depends on. Any successful add or
   remove changes it, so memoising on it cannot deadlock a genuine change. */
function cartToken(cart) {
  if (!cart) return '';
  return cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
}

function tierHasChoices(tier) {
  if (typeof tier.needsPicker === 'boolean') return tier.needsPicker;
  return tier.choices.length > 1;
}

/* Whether this tier gives its gift away with no shopper action. Mirrors
   t_auto_add in cart-tier-rewards.liquid: just the merchant's Auto Add Free
   Gift switch. A tier with colours or sizes and the switch on gains its
   configured Free Gift variant, and the claimed line offers "Change selection"
   so the shopper is never stuck with it.

   A payload predating the switch carries no autoAdd key at all — that happens
   when component-cart-drawer.js re-renders from a section cached before the
   deploy. Falling back to the old rule (nothing to pick) is deliberately
   conservative: a stale payload never auto-adds a variant the shopper might not
   want, and the placeholder that stale markup renders still works. */
function tierAutoAdds(tier) {
  if (typeof tier.autoAdd === 'boolean') return tier.autoAdd;
  return !tierHasChoices(tier);
}

/* Which variant this tier should currently be giving away: its Free Gift if
   that is available, otherwise the first available Product Reference. Used by
   the auto-add path only — a tier whose switch is off waits for the shopper.

   The fallback matters more now that tiers with choices can auto-add: when the
   merchant's nominated variant is sold out there is no placeholder to fall back
   on (auto-add suppresses it), so without this the gift would vanish silently
   with no way for the shopper to recover it. A different colour they can swap
   beats no gift at all. */
function pickVariant(tier) {
  if (tier.variantId && tier.giftAvailable && !failedVariants.has(String(tier.variantId))) {
    return String(tier.variantId);
  }
  const choice = tier.choices.find(
    (option) => option.available && !failedVariants.has(String(option.variantId))
  );
  return choice ? String(choice.variantId) : null;
}

/**
 * Diff the gift lines the cart has against the gift lines it should have.
 * @returns {{remove: string[], add: Array<{tierId: string, variantId: string}>}}
 */
function buildPlan(config, cart) {
  const total = qualifyingTotal(cart, config);
  const giftLines = cart.items.filter((item) => isGiftLine(item, config));
  const knownTierIds = new Set(config.tiers.map((tier) => String(tier.id)));

  const remove = [];
  const add = [];
  const setQty = [];

  config.tiers.forEach((tier) => {
    const allowed = new Set(
      [tier.variantId, ...tier.choices.map((option) => option.variantId)]
        .filter(Boolean)
        .map(String)
    );
    if (!allowed.size) return; // free-shipping tier: nothing to give

    /* Matching on PRODUCT, not variant, is what lets a shopper keep a size they
       chose in the gift picker: `choices` lists only one variant per colour, so
       a re-sized line looks like a variant this tier does not offer and would
       otherwise be reverted on the next pass. Sold-out sizes need no handling
       here — the picker renders them disabled, so they cannot be chosen. */
    const allowedProducts = new Set(
      [tier.productId, ...tier.choices.map((option) => option.productId)]
        .filter(Boolean)
        .map(String)
    );

    const lines = giftLines.filter(
      (line) => String(line.properties[config.tierProperty]) === String(tier.id)
    );

    if (total < tier.threshold) {
      lines.forEach((line) => remove.push(line.key));
      return;
    }

    const isOffered = (line) =>
      allowed.has(String(line.variant_id)) || allowedProducts.has(String(line.product_id));

    /* Keep one line this tier still offers; drop the rest, so a duplicate or a
       product the merchant has since removed gets cleaned up. A shopper-chosen
       variant wins over the tier default, so the leftover default is the line
       that goes when a swap left both behind. */
    const offered = lines.filter(isOffered);
    const keep =
      offered.find((line) => String(line.variant_id) !== String(tier.variantId)) || offered[0];

    lines.forEach((line) => {
      if (line !== keep) remove.push(line.key);
    });

    if (keep && Number(keep.quantity) !== 1) {
      setQty.push(keep.key);
    }

    /* Auto-add whenever the merchant asked for it. For a tier with colour or
       size options that means the configured Free Gift variant goes in and the
       claimed line offers "Change selection"; the shopper is never stuck with
       it. A tier whose switch is off gains nothing here — it renders a
       placeholder row and waits for the claim or the picker.

       Must stay in step with t_auto_add in cart-tier-rewards.liquid, which is
       what component-cart-drawer.liquid inverts to decide whether that row
       renders at all — if they disagree, a tier either shows a row for a gift
       already in the cart (two gifts) or silently never gives its gift. */
    if (!keep && tierAutoAdds(tier)) {
      const wanted = pickVariant(tier);
      if (wanted) add.push({ tierId: String(tier.id), variantId: wanted });
    }
  });

  /* Gift lines whose tier metaobject has been deleted or unlinked. */
  giftLines.forEach((line) => {
    if (!knownTierIds.has(String(line.properties[config.tierProperty]))) {
      remove.push(line.key);
    }
  });

  return { remove: [...new Set(remove)], add, setQty: [...new Set(setQty)] };
}

function addGift(variantId, tierId, config, withSections) {
  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  body.append(`properties[${config.giftProperty}]`, 'true');
  body.append(`properties[${config.tierProperty}]`, String(tierId));

  return window.Cart.add(body, SOURCE, { withSections }).catch((error) => {
    if (error.status === 422) failedVariants.add(String(variantId));
    throw error;
  });
}

async function applyPlan(plan, config) {
  const ops = [];

  /* Removals go by line item KEY, not index: dropping a line renumbers every
     line after it, and cart.js holds line targets across a 250ms debounce. */
  plan.remove.forEach((key) => {
    ops.push((withSections) =>
      window.Cart.change({ id: key, quantity: 0 }, SOURCE, { withSections })
    );
  });
  (plan.setQty || []).forEach((key) => {
    ops.push((withSections) =>
      window.Cart.change({ id: key, quantity: 1 }, SOURCE, { withSections })
    );
  });
  plan.add.forEach((entry) => {
    ops.push((withSections) => addGift(entry.variantId, entry.tierId, config, withSections));
  });

  if (!ops.length) return false;

  for (let i = 0; i < ops.length; i += 1) {
    if (opsBudget <= 0) {
      console.warn('[cart-tier-rewards] operation budget exhausted; stopping.');
      return true;
    }
    opsBudget -= 1;
    try {
      await ops[i](i === ops.length - 1);
    } catch (error) {
      console.warn('[cart-tier-rewards]', error.status || '', error.message);
    }
  }
  return true;
}

async function reconcile() {
  const root = document.querySelector('cart-tier-rewards');
  if (!root) return;
  if (!window.Cart || !window.Cart.state) return;

  const config = readPayload(root);
  if (!config || !config.tiers || !config.tiers.length) return;

  if (reconciling) {
    rerunRequested = true;
    return;
  }

  const token = cartToken(window.Cart.state);
  if (token === settledToken) return; // nothing has changed since we last settled

  reconciling = true;
  /* Per reconcile pass, not per page load.

     opsBudget was initialised once at module scope and never reset, so it was a
     lifetime cap: after 12 gift mutations the engine stopped applying plans for
     the rest of the page's life while still marking each cart as settled. Six
     threshold crossings is an ordinary session, and the result was a shopper
     keeping a gift they no longer qualified for, or losing one they had earned,
     until they reloaded. That is CG-01 in the cart gamification UAT.

     Reset here rather than inside the do/while below: the loop re-runs whenever
     a trigger lands mid-flight, and refilling the budget on each iteration would
     let a runaway add/remove cycle spin without limit, which is the case the cap
     exists to catch. One budget per reconcile invocation keeps that guard and
     gives each new shopper action a fresh allowance. */
  opsBudget = MAX_OPS;
  try {
    do {
      rerunRequested = false;
      const didWork = await applyPlan(buildPlan(config, window.Cart.state), config);
      /* Claimed only once a pass has actually evaluated this cart. Marking it
         settled up front meant a pass that ran against a stale Cart.state could
         memoise a cart it never really reconciled, leaving an unlocked gift
         missing until the next mutation. */
      settledToken = didWork ? cartToken(window.Cart.state) : token;
    } while (rerunRequested);
  } catch (error) {
    console.warn('[cart-tier-rewards] reconcile failed:', error.message);
  } finally {
    reconciling = false;
  }
}

function scheduleReconcile() {
  if (scheduled) return;
  scheduled = true;
  const run = () => {
    scheduled = false;
    reconcile();
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 500 });
  } else {
    setTimeout(run, 0);
  }
}

function createDrawerConfettiCanvas(panel) {
  const rect = panel.getBoundingClientRect();
  const canvas = document.createElement('canvas');
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;
  canvas.style.zIndex = '2';
  canvas.style.pointerEvents = 'none';
  panel.appendChild(canvas);
  return canvas;
}

const PIECES_PER_DROP = 3;

let rectangleShape;

function shapes() {
  if (rectangleShape === undefined) {
    try {
      rectangleShape = window.confetti.shapeFromPath({
        path: 'M0 0 H14 V8 H0 Z',
        matrix: [0.7, 0, 0, 0.7, -4.9, -2.8],
      });
    } catch (error) {
      rectangleShape = 'square';
    }
  }
  return ['square', 'circle', rectangleShape];
}

function dropConfetti(fire) {
  const available = shapes();

  for (let i = 0; i < PIECES_PER_DROP; i += 1) {
    fire({
      particleCount: 1,
      startVelocity: 7,
      spread: 0,
      angle: 270,
      decay: 1,
      gravity: 1.0,
      origin: { x: Math.random(), y: 0 },
      drift: Math.random() * 1.4 - 0.7,
      scalar: 1.1 + Math.random() * 0.5,
      ticks: 260 + Math.floor(Math.random() * 60),
      shapes: [available[Math.floor(Math.random() * available.length)]],
      colors: [CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]],
      disableForReducedMotion: true,
    });
  }
}

function fireConfettiShower(root) {
  if (typeof window.confetti !== 'function' || typeof window.confetti.create !== 'function') {
    console.warn('[cart-tier-rewards] canvas-confetti not loaded; skipping confetti.');
    return;
  }

  const panel = root.closest('.drawer__wrapper') || root;
  if (getComputedStyle(panel).position === 'static') panel.style.position = 'relative';

  const canvas = createDrawerConfettiCanvas(panel);
  const fire = window.confetti.create(canvas, { resize: true, useWorker: false });

  /* Small batches on an interval, rather than one big drop, is what makes the
     fall read as continuous. */
  const EMIT_EVERY_MS = 110;
  const EMIT_FOR_MS = 2000;
  /* Must outlast the final batch: emitted at EMIT_FOR_MS, then alive for up to
     ~320 ticks (~5.3s at 60fps). Tearing the canvas down earlier would clip
     those pieces mid-fall. */
  const CANVAS_LIFETIME_MS = EMIT_FOR_MS + 5500;

  const emitter = setInterval(() => dropConfetti(fire), EMIT_EVERY_MS);
  dropConfetti(fire);

  setTimeout(() => clearInterval(emitter), EMIT_FOR_MS);
  setTimeout(() => {
    clearInterval(emitter);
    canvas.remove();
  }, CANVAS_LIFETIME_MS);
}

function fireMilestoneConfetti(root, config) {
  const reachedNow = new Set(
    config.tiers.filter((tier) => tier.reached).map((tier) => String(tier.id))
  );

  if (lastReachedTierIds) {
    const hasNewlyReached = [...reachedNow].some((tierId) => !lastReachedTierIds.has(tierId));
    if (hasNewlyReached) fireConfettiShower(root);
  }

  lastReachedTierIds = reachedNow;
}

export class CartTierRewards extends HTMLElement {
  connectedCallback() {
    this.animateFill();

    const config = readPayload(this);
    if (config && config.tiers) fireMilestoneConfetti(this, config);

    /* Primary trigger. Fires on first paint and again on every drawer
       re-render, which is exactly when the cart has changed. */
    scheduleReconcile();
  }

  /* The element is replaced wholesale on re-render, so the new fill node is born
     at its final width and `transition: width` never fires. Start it from the
     previous pass's value (module scope) and let the transition run. */
  animateFill() {
    const target = parseFloat(this.dataset.progress || '0');
    if (Number.isNaN(target)) return;

    this.style.setProperty('--ctr-progress', `${lastProgress}%`);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.setProperty('--ctr-progress', `${target}%`);
      });
    });
    lastProgress = target;
  }
}

if (!customElements.get('cart-tier-rewards')) {
  customElements.define('cart-tier-rewards', CartTierRewards);
}

/* Secondary trigger, registered at module-execution time so it is never removed
   and therefore always fires. Covers cart changes that do not re-render the
   drawer — e.g. Cart.update with { withSections: false }. */
document.addEventListener('cart:change', (event) => {
  if (event.detail?.source === SOURCE) return; // our own work
  scheduleReconcile();
});

/* cart.js is a head module so window.Cart normally exists by now; if it does
   not, wait for the first cart event rather than giving up. */
if (!window.Cart) {
  document.addEventListener('cart:change', scheduleReconcile, { once: true });
}
